import { supabase } from "./supabase";

const CARD_IMAGE_BUCKET = "card-images";
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];
const SIGNED_URL_TTL_SECONDS = 60 * 10;

type CardImageSlot = "front" | "back" | "other";

type SignedUrlCacheEntry = {
  url: string;
  expiresAt: number;
};

const signedUrlCache = new Map<string, SignedUrlCacheEntry>();

const getFileExtension = (file: File) => {
  const nameExtension = file.name.split(".").pop();
  if (nameExtension) {
    return nameExtension.toLowerCase();
  }
  if (file.type === "image/jpeg") return "jpg";
  if (file.type === "image/png") return "png";
  if (file.type === "image/webp") return "webp";
  return "jpg";
};

const getUserId = async () => {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return data.user?.id;
};

const validateImageFile = (file: File) => {
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    throw new Error("Unsupported file type. Use JPEG, PNG, or WebP.");
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    throw new Error("Image is too large. Max size is 10 MB.");
  }
};

export const uploadCardImage = async (file: File, transactionId: string, slot: CardImageSlot) => {
  validateImageFile(file);
  const userId = await getUserId();
  if (!userId) {
    throw new Error("User not authenticated");
  }

  const extension = getFileExtension(file);
  const filePath = `${userId}/${transactionId}/${slot}/${crypto.randomUUID()}.${extension}`;

  const { error } = await supabase.storage
    .from(CARD_IMAGE_BUCKET)
    .upload(filePath, file, {
      contentType: file.type,
      upsert: true,
    });

  if (error) {
    throw error;
  }

  return filePath;
};

export const deleteCardImages = async (paths: Array<string | null | undefined>) => {
  const uniquePaths = Array.from(new Set(paths.filter(Boolean))) as string[];
  if (uniquePaths.length === 0) {
    return;
  }

  const { error } = await supabase.storage.from(CARD_IMAGE_BUCKET).remove(uniquePaths);
  if (error) {
    throw error;
  }
};

export const getSignedImageUrl = async (path: string) => {
  const cached = signedUrlCache.get(path);
  const now = Date.now();
  if (cached && cached.expiresAt > now) {
    return cached.url;
  }

  const { data, error } = await supabase.storage
    .from(CARD_IMAGE_BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);

  if (error) {
    throw error;
  }

  const url = data.signedUrl;
  signedUrlCache.set(path, {
    url,
    expiresAt: now + SIGNED_URL_TTL_SECONDS * 1000,
  });

  return url;
};

export const getSignedImageUrls = async (paths: string[]) => {
  return Promise.all(paths.map((path) => getSignedImageUrl(path)));
};
